<template>
  <InfuseTable
    :click-permission-id="centralSystemPermissions.GET"
    :edit-permission-id="centralSystemPermissions.UPDATE"
    :remove-permission-id="centralSystemPermissions.DELETE"
    :columns="columns"
    :rows="rows"
    :loading="loading"
    :editable="editable"
    :removable="removable"
    :search="search"
    :has-more-items="hasMoreItems"
    :disabled-rows="disabledRows"
    @click-row="(row: CentralSystem) => row.readonly || emit('click-row', row)"
    @load="emit('load')"
    @remove="(row: CentralSystem) => emit('remove', row)"
    @edit="(row: CentralSystem) => emit('edit', row)"
    @search="(query: string) => emit('search', query)"
  >
    <template #actions>
      <InfuseButton
        v-permission="{ id: centralSystemPermissions.CREATE, hide: false }"
        v-if="createEnabled"
        class="w-64"
        size="medium"
        @click="emit('create')"
      >
        {{ t('e_connector.centralSystems.table.create_button') }}
      </InfuseButton>
    </template>

    <template #uri="{ data }">
      <span class="max-w-[400px] truncate">{{ data || '-' }}</span>
    </template>

    <template #type="{ data }">
      {{ data || '-' }}
    </template>

    <template #ocppVersion="{ data }">
      <InfuseTag v-if="data" variant="neutral">OCPP-{{ data }}</InfuseTag>
    </template>
  </InfuseTable>
</template>

<script lang="ts" setup>
import { InfuseButton, InfuseTable, InfuseTag } from '@infuse/ui/components';
import { vPermission } from '@infuse/iam/permission/directives';
import type { UiTableColumn } from '@infuse/ui/domain';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { CentralSystem } from '@infuse/e-connector/central-system/domain';
import { centralSystemPermissions } from '@infuse/api/e-connector/central-system';

interface Emits {
  (e: 'click-row', row: CentralSystem): void;
  (e: 'create'): void;
  (e: 'load'): void;
  (e: 'remove', row: CentralSystem): void;
  (e: 'edit', row: CentralSystem): void;
  (e: 'search', query: string): void;
}

const emit = defineEmits<Emits>();

interface Props {
  createEnabled: boolean;
  editable: boolean;
  loading?: boolean;
  removable?: boolean;
  rows: CentralSystem[];
  searchEnabled: boolean;
  searchQuery?: string;
  hasMoreItems: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  createEnabled: false,
  editable: false,
  loading: false,
  removable: false,
  searchEnabled: true,
  searchQuery: ''
});

const { t } = useI18n();

const search = computed(() =>
  !props.searchEnabled
    ? undefined
    : {
        placeholder: t('e_connector.centralSystems.table.search'),
        query: props.searchQuery
      }
);

const disabledRows = computed(() => props.rows.filter((row) => row.readonly));

const columns: UiTableColumn[] = [
  {
    name: 'name',
    label: () => t('e_connector.common.centralSystem.name'),
    field: 'name',
    bold: true
  },
  {
    name: 'uri',
    label: () => t('e_connector.common.centralSystem.url'),
    field: 'uri',
    copyable: true
  },
  {
    name: 'type',
    label: () => t('e_connector.common.centralSystem.type'),
    field: 'type'
  },
  {
    name: 'ocppVersion',
    label: () => t('e_connector.common.centralSystem.ocppVersion'),
    field: 'ocppVersion'
  }
];
</script>
